/**
 * マークダウン記法を除去してテキストを整形するユーティリティ
 * @version 2.0 - Fixed title line removal issue
 */

/**
 * マークダウン記法を除去してテキストを整形する
 * @param text - 変換対象のマークダウンテキスト
 * @returns 整形されたプレーンテキスト
 */
export const cleanMarkdownText = (text: string): string => {
  if (!text) return '';

  try {
    let cleanedText = text;

    // 0. コードブロックを一時的に保護
    // BCP文書にコードが含まれる場合、内部の記号を保護する
    const codeBlocks: string[] = [];
    cleanedText = cleanedText.replace(/```[\s\S]*?```/g, (match) => {
      codeBlocks.push(match);
      return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
    });

    // インラインコードも保護
    const inlineCodes: string[] = [];
    cleanedText = cleanedText.replace(/`([^`]+)`/g, (match) => {
      inlineCodes.push(match);
      return `__INLINE_CODE_${inlineCodes.length - 1}__`;
    });

    // 1. ヘッダー記法を除去 (###, ##, #)
    cleanedText = cleanedText.replace(/^#{1,6}\s*/gm, '');

    // 2. リスト記法を整形 (箇条書き記号を・に統一)
    // IMPORTANT: 太字・斜体除去の前に実行して箇条書きマーカーを保護
    // IMPORTANT: インデント付き箇条書きを先に処理してネスト構造を保持
    cleanedText = cleanedText.replace(/^([ \t]+)[-*+•]\s+/gm, '$1・');
    cleanedText = cleanedText.replace(/^[-*+•]\s+/gm, '・');

    // 3. 太字記法を除去 (**text**)
    cleanedText = cleanedText.replace(/\*\*(.*?)\*\*/g, '$1');

    // 4. 斜体記法を除去 (*text*)
    cleanedText = cleanedText.replace(/\*([^*]+)\*/g, '$1');

    // 5. テーブル記法を整形
    cleanedText = cleanedText.replace(/\|/g, ' | ');
    cleanedText = cleanedText.replace(/\|\s*-+\s*\|/g, ''); // テーブルの区切り行を除去
    cleanedText = cleanedText.replace(/^\s*\|\s*$/gm, ''); // 空のテーブル行を除去

    // 6. 番号付きリストの整形
    cleanedText = cleanedText.replace(/^\d+\.\s+/gm, '');

    // 7. 引用記法を除去
    cleanedText = cleanedText.replace(/^>\s*/gm, '');

    // 8. リンク記法を除去 [text](url) → text
    cleanedText = cleanedText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    // 9. 画像記法を除去 ![alt](url)
    cleanedText = cleanedText.replace(/!\[[^\]]*\]\([^)]+\)/g, '');

    // 10. 水平線と水平線以降の内容を除去 (メタデータや指示文を除去)
    const horizontalRuleIndex = cleanedText.search(/^---+\s*$/m);
    if (horizontalRuleIndex !== -1) {
      cleanedText = cleanedText.substring(0, horizontalRuleIndex);
    }

    // 11. 複数の改行を2つまでに制限
    cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n');

    // 12. 行末の空白を除去(空行は手順11で制限済みのため保持)
    cleanedText = cleanedText.split('\n')
      .map(line => line.replace(/\s+$/g, '')) // 行末のスペースのみ削除
      .join('\n');

    // 13. 特殊文字のエスケープを除去
    cleanedText = cleanedText.replace(/\\(.)/g, '$1');

    // 14. コードブロックを復元
    cleanedText = cleanedText.replace(/__CODE_BLOCK_(\d+)__/g, (_, index) => {
      return codeBlocks[parseInt(index)];
    });
    cleanedText = cleanedText.replace(/__INLINE_CODE_(\d+)__/g, (_, index) => {
      return inlineCodes[parseInt(index)];
    });

    return cleanedText.trim();
  } catch (error) {
    console.warn('マークダウンクリーニングでエラー:', error);
    return text;
  }
};

/**
 * BCP文書から特定のセクションを抽出してマークダウンを除去する
 * @param bcpDocument - BCP文書全体
 * @param sectionKeywords - セクションを特定するキーワード配列
 * @returns 抽出・整形されたセクション内容
 */
export const extractAndCleanSection = (bcpDocument: string, sectionKeywords: string[]): string => {
  try {
    const lines = bcpDocument.split('\n');
    let extractedContent = '';
    let isInSection = false;
    let sectionDepth = 0;
    let foundSectionStart = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // セクションの開始を検出（複数のキーワードをチェック）
      const hasKeyword = sectionKeywords.some(keyword => 
        line.toLowerCase().includes(keyword.toLowerCase())
      );
      
      // より厳密なセクション開始判定
      if (hasKeyword && (line.includes('#') || line.includes('**'))) {
        // 特別なケース: "基本版 BCP規程 3-1 リスクの最小化" の場合、その下の内容も含める
        if (line.includes('基本版 BCP規程') && line.includes('リスクの最小化')) {
          isInSection = true;
          foundSectionStart = true;
          sectionDepth = (line.match(/#{1,6}/g)?.[0]?.length || 1);
          extractedContent += line + '\n'; // この場合はヘッダーも含める
          continue;
        } else {
          isInSection = true;
          foundSectionStart = true;
          sectionDepth = (line.match(/#{1,6}/g)?.[0]?.length || 1);
          continue; // 通常はヘッダー行は含めない
        }
      }
      
      // セクション内の場合
      if (isInSection && foundSectionStart) {
        // 水平線（---）で終了
        if (line.match(/^-{3,}\s*$/)) {
          break;
        }
        
        // 特別なケース: "基本版 BCP規程 3-1 リスクの最小化" のセクション内では、
        // 子セクション（### 9-1）は含める
        const isInBCPRiskSection = extractedContent.includes('基本版 BCP規程') && extractedContent.includes('リスクの最小化');
        
        // 次のセクションヘッダーを検出（番号付きセクション）
        const nextSectionMatch = line.match(/^#+\s*(\d+-\d+|\d+\.)/);
        const isNextSection = nextSectionMatch !== null;
        
        // マークダウンヘッダーでの次セクション検出
        const currentDepth = (line.match(/^#{1,6}/g)?.[0]?.length || 0);
        const isHeaderAtSameLevel = currentDepth > 0 && currentDepth <= sectionDepth;
        
        // 基本版 BCP規程セクション内の場合、### 9-1 のような子セクションは含める
        if (isInBCPRiskSection && line.match(/^###\s+9-1\s+/)) {
          extractedContent += line + '\n';
          continue;
        }
        
        // 明確な次セクションまたは同レベルヘッダーで終了
        if (isNextSection || (isHeaderAtSameLevel && !hasKeyword)) {
          // ただし、基本版 BCP規程セクション内の ### 9-1 は除外しない
          if (!(isInBCPRiskSection && line.match(/^###\s+9-1\s+/))) {
            break;
          }
        }
        
        // 内容を追加（空行は除く）
        if (line.trim() !== '') {
          extractedContent += line + '\n';
        }
      }
    }
    
    // 抽出内容が不十分な場合は、より柔軟な検索を試行
    if (extractedContent.trim().length < 50) {
      console.log(`⚠️ Section content too short for keywords: ${sectionKeywords.join(', ')}, trying fallback...`);
      
      // フォールバック1: キーワードを含む段落全体を抽出
      const paragraphs = bcpDocument.split(/\n\s*\n/);
      for (const paragraph of paragraphs) {
        const hasAnyKeyword = sectionKeywords.some(keyword => 
          paragraph.toLowerCase().includes(keyword.toLowerCase())
        );
        
        if (hasAnyKeyword && paragraph.trim().length > extractedContent.trim().length) {
          extractedContent = paragraph;
          break;
        }
      }
      
      // フォールバック2: キーワードを含む行とその前後の文脈を抽出
      if (extractedContent.trim().length < 30) {
        const lines = bcpDocument.split('\n');
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const hasAnyKeyword = sectionKeywords.some(keyword => 
            line.toLowerCase().includes(keyword.toLowerCase())
          );
          
          if (hasAnyKeyword) {
            // 該当行とその前後5行を抽出
            const startIndex = Math.max(0, i - 2);
            const endIndex = Math.min(lines.length, i + 10);
            const contextLines = lines.slice(startIndex, endIndex);
            const contextContent = contextLines.join('\n').trim();
            
            if (contextContent.length > extractedContent.trim().length) {
              extractedContent = contextContent;
              break;
            }
          }
        }
      }
    }
    
    // マークダウン記法を除去して返す
    const cleaned = cleanMarkdownText(extractedContent);
    console.log(`📄 Extracted section for [${sectionKeywords[0]}]: ${cleaned.substring(0, 100)}...`);
    return cleaned;
  } catch (error) {
    console.warn('セクション抽出でエラー:', error);
    return '';
  }
};

/**
 * 特定のセクション番号に基づいてコンテンツを抽出する
 * @param bcpDocument - BCP文書全体
 * @param sectionNumber - セクション番号（例: "2-2", "3-1"）
 * @returns 抽出されたセクション内容
 */
export const extractSectionByNumber = (bcpDocument: string, sectionNumber: string): string => {
  try {
    console.log(`🔍 Looking for section: ${sectionNumber}`);
    
    // セクション番号をエスケープして正規表現を作成
    const escapedNumber = sectionNumber.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    
    // BCP文書の実際の形式に対応したセクション検出パターン
    const sectionPatterns = [
      new RegExp(`^###\\s+${escapedNumber}\\s+.*$`, 'gm'),       // ### 7-1 タイトル (最も一般的)
      new RegExp(`^##\\s+${escapedNumber}\\.\\s+.*$`, 'gm'),     // ## 5. タイトル (ドット付き)
      new RegExp(`^##\\s+${escapedNumber}\\s+.*$`, 'gm'),        // ## 2-2 タイトル
      new RegExp(`^#\\s+${escapedNumber}\\s+.*$`, 'gm'),         // # 1-1 タイトル
      new RegExp(`^\\*\\*${escapedNumber}\\s+.*\\*\\*$`, 'gm'),  // **2-2 タイトル**
      new RegExp(`^${escapedNumber}\\s+.*$`, 'gm'),              // 2-2 タイトル（プレーンテキスト）
      new RegExp(`^\\*\\*${escapedNumber}\\*\\*`, 'gm')          // **2-2**
    ];
    
    // Special handling for major sections with standalone numbering
    if (sectionNumber === '4') {
      sectionPatterns.unshift(
        new RegExp(`^##\\s+4\\.?\\s+BCP発動基準`, 'gm')
      );
    }
    if (sectionNumber === '5') {
      sectionPatterns.unshift(
        new RegExp(`^##\\s+5\\.?\\s+BCP発動基準`, 'gm')  // ## 5. BCP発動基準 or ## 5 BCP発動基準
      );
    }
    
    let sectionStart = -1;
    let matchedPattern = '';
    let sectionHeaderEnd = -1;
    
    // セクション開始位置を検索
    for (const pattern of sectionPatterns) {
      pattern.lastIndex = 0; // 正規表現のインデックスをリセット
      const match = pattern.exec(bcpDocument);
      if (match) {
        sectionStart = match.index;
        matchedPattern = match[0];
        sectionHeaderEnd = sectionStart + matchedPattern.length;
        console.log(`✅ Found section with pattern: "${matchedPattern}"`);
        break;
      }
    }
    
    if (sectionStart === -1) {
      // Only log as error if it's an expected section (not 8-x or 9-x which don't exist)
      const [mainNum] = sectionNumber.split('-').map(n => parseInt(n));
      if (!isNaN(mainNum) && mainNum >= 8) {
        console.log(`ℹ️ Section ${sectionNumber} does not exist in document structure`);
      } else {
        console.log(`❌ Section ${sectionNumber} not found`);
      }
      return '';
    }
    
    // セクション内容の開始位置（タイトル行の次の行から開始）
    let contentStart = sectionHeaderEnd; // FIXED 2025-06-15: Skip title line
    
    // タイトル行直後の改行文字をスキップ
    while (contentStart < bcpDocument.length && 
           (bcpDocument[contentStart] === '\n' || bcpDocument[contentStart] === '\r')) {
      contentStart++;
    }
    
    // 次のセクション検出のための正規表現（BCP文書の実際の形式に対応）
    const afterContentText = bcpDocument.substring(contentStart);
    
    // 番号の次の値を計算（例: 7-1 → 7-2, 3-3 → 4-1）
    const [mainNum, subNum] = sectionNumber.split('-').map(n => parseInt(n));
    const nextSectionNumbers = [
      `${mainNum}-${subNum + 1}`,  // 同じメインセクションの次（7-1 → 7-2）
      `${mainNum + 1}-1`,          // 次のメインセクションの開始（7-5 → 8-1）
      `${mainNum + 1}`,            // 次のメインセクション（単純番号）
    ];
    
    // For section 1-1, we need to catch ANY higher major section (2, 3, 4, 5, 6, 7, 8, 9...)
    if (sectionNumber === '1-1') {
      for (let i = 2; i <= 9; i++) {
        nextSectionNumbers.push(`${i}`);
        nextSectionNumbers.push(`${i}-1`);
      }
    }
    
    // For section 4-2, we specifically need to catch section 5 and beyond
    if (sectionNumber === '4-2') {
      console.log(`🔍 Special handling for section 4-2, looking for sections 5 and beyond`);
    }
    
    // Get current section depth for better boundary detection
    const currentSectionDepth = matchedPattern.match(/^#{1,6}/)?.[0]?.length || 3;
    
    // より正確な次セクションパターン検出
    const nextSectionPatterns = [
      // 水平線（セクション区切り）を最優先
      /^-{3,}\s*$/m,              // --- 形式の区切り線
      
      // Major sections with dot (e.g., "## 7. 事業継続のための...") を優先
      /^##\s+\d+\.\s+/m,          // ## 形式のメインセクション with dot
      /^#\s+\d+\.\s+/m,           // # 形式のメインセクション with dot
      
      // Special patterns for major sections with period (e.g., "## 7. 事業継続のための...")
      ...nextSectionNumbers.filter(num => !num.includes('-')).map(num => new RegExp(`^##\\s+${num}\\.\\s+`, 'm')),
      
      // 具体的な次のセクション番号
      ...nextSectionNumbers.map(num => new RegExp(`^###\\s+${num.toString().replace('-', '\\-')}\\s+`, 'm')),
      ...nextSectionNumbers.map(num => new RegExp(`^##\\s+${num.toString().replace('-', '\\-')}\\s+`, 'm')),
      ...nextSectionNumbers.map(num => new RegExp(`^#\\s+${num.toString().replace('-', '\\-')}\\s+`, 'm')),
      
      // PRIORITY 2: Major section headers
      /^##\s+基本版/m,            // ## 基本版 で始まる新しい主要セクション
      /^##\s+\d+\./m,             // ## N. 形式の主要セクション
      
      // PRIORITY 3: Same or higher level headers (## if current is ###)
      ...(currentSectionDepth >= 3 ? [/^##\s+/m] : []),
      ...(currentSectionDepth >= 2 ? [/^#\s+/m] : []),
      
      // Special pattern for section 5 (which has no subsection)
      /^##\s+5\.?\s+BCP発動基準/m,  // ## 5. BCP発動基準 or ## 5 BCP発動基準
      
      // 任意の番号付きセクション（現在のセクション以降）
      /^###\s+\d+-\d+\s+/m,       // ### 形式の任意のセクション
      /^##\s+\d+-\d+\s+/m,        // ## 形式の任意のセクション  
      /^#\s+\d+-\d+\s+/m,         // # 形式の任意のセクション
      /^###\s+\d+\s+/m,           // ### 形式のメインセクション
      /^##\s+\d+\s+/m,            // ## 形式のメインセクション
      /^#\s+\d+\s+/m,             // # 形式のメインセクション
      
      // その他のセクション区切り - 特定の場合のみ適用
      /^##\s+基本版\s+/m,         // "## 基本版" で始まる新セクション
    ];
    
    // Find all matches and choose the one with the smallest index
    let sectionEnd = afterContentText.length;
    let foundEndPattern = '';
    let bestMatch: { index: number; pattern: string; priority: number } | null = null;
    
    // First pass: collect all matches
    const allMatches: Array<{ index: number; pattern: string; priority: number }> = [];
    
    nextSectionPatterns.forEach((pattern, patternIndex) => {
      pattern.lastIndex = 0;
      const match = pattern.exec(afterContentText);
      if (match) {
        // Debug logging for section 1-1
        if (sectionNumber === '1-1' && match[0].includes('7')) {
          console.log(`🔍 Found potential section boundary: "${match[0].trim()}" at index ${match.index}`);
        }
        
        allMatches.push({
          index: match.index,
          pattern: match[0],
          priority: patternIndex
        });
      }
    });
    
    // Sort by index (closest first) and then by priority
    allMatches.sort((a, b) => {
      if (a.index !== b.index) return a.index - b.index;
      return a.priority - b.priority;
    });
    
    // Process matches in order
    for (const match of allMatches) {
      const foundNumber = match.pattern.match(/(\d+(-\d+)?)/)?.[1];
      
      // Special handling for section 5 detection when we're in section 4-x
      if (sectionNumber.startsWith('4-') && match.pattern.match(/^##\s+5\.?\s+BCP発動基準/)) {
        sectionEnd = match.index;
        foundEndPattern = match.pattern;
        console.log(`🛑 Found section 5 (BCP発動基準) at index ${match.index}`);
        break;
      }
      
      // Check if it's a horizontal rule (---)
      if (match.pattern.match(/^-{3,}\s*$/m)) {
        // Don't break immediately for ---, continue checking for better matches
        if (!bestMatch || match.index < bestMatch.index) {
          bestMatch = match;
        }
        continue;
      }
        
      // If we found a section number, check if it's later
      if (foundNumber) {
        if (isLaterSection(sectionNumber, foundNumber)) {
          sectionEnd = match.index;
          foundEndPattern = match.pattern;
          console.log(`🛑 Found next section: ${foundNumber} at index ${match.index}`);
          break; // Stop at the first valid next section
        } else {
          console.log(`⚠️ Found section ${foundNumber} but it's not later than ${sectionNumber}`);
        }
      } else if (match.pattern.match(/^##\s+\d+\.\s+/m)) {
        // For major section headers with dot like "## 7. 事業継続のための..."
        const majorSectionNum = match.pattern.match(/##\s+(\d+)\./)?.[1];
        if (majorSectionNum && isLaterSection(sectionNumber, majorSectionNum)) {
          sectionEnd = match.index;
          foundEndPattern = match.pattern;
          console.log(`🛑 Found major section: ${majorSectionNum} at index ${match.index}`);
          break;
        }
      } else if (match.pattern.match(/^##\s+基本版\s+/m)) {
        // For major section headers like "## 基本版", always treat as section end
        sectionEnd = match.index;
        foundEndPattern = match.pattern;
        console.log(`🛑 Found major section header: ${match.pattern.trim()} at index ${match.index}`);
        break;
      }
    }
    
    // If we only found ---, use it as fallback
    if (sectionEnd === afterContentText.length && bestMatch) {
      sectionEnd = bestMatch.index;
      foundEndPattern = bestMatch.pattern;
      console.log(`🛑 Found section separator (---) at index ${bestMatch.index}`);
    }
    
    // セクション内容を抽出
    let rawContent = afterContentText.substring(0, sectionEnd);
    
    // 末尾の区切り線（---）を除去
    rawContent = rawContent.replace(/\n---\s*$/m, '').trim();
    
    console.log(`📄 Section ${sectionNumber}:`);
    console.log(`   Start: ${sectionStart}, Content start: ${contentStart}`);
    console.log(`   Content length: ${rawContent.length} chars`);
    console.log(`   End pattern: "${foundEndPattern}"`);
    console.log(`   Preview: "${rawContent.substring(0, 100)}..."`);
    
    // タイトル行は既にcontentStart計算時に除外済み
    console.log(`🔧 Title line removal fixed v2.0`); // Force hash change
    
    // 内容が空または短すぎる場合はフォールバックを試行
    if (rawContent.length < 10) {
      console.log(`⚠️ Content too short, trying alternative extraction...`);
      return extractSectionFallback(bcpDocument, sectionNumber);
    }
    
    return cleanMarkdownText(rawContent);
  } catch (error) {
    console.warn(`セクション${sectionNumber}の抽出でエラー:`, error);
    return '';
  }
};

/**
 * セクション番号の大小比較（2つ目が1つ目より後のセクションかどうか）
 */
const isLaterSection = (currentSection: string, foundSection: string): boolean => {
  // Handle sections with and without subsections
  const parseSection = (section: string) => {
    const parts = section.split('-').map(n => parseInt(n));
    return { main: parts[0] || 0, sub: parts[1] || 0 };
  };
  
  const current = parseSection(currentSection);
  const found = parseSection(foundSection);
  
  // Debug logging for section 4-2
  if (currentSection === '4-2') {
    console.log(`📊 Comparing ${currentSection} (main: ${current.main}, sub: ${current.sub}) with ${foundSection} (main: ${found.main}, sub: ${found.sub})`);
  }
  
  // Special case: if current is 1-x and found is any major section >= 2, it's later
  if (current.main === 1 && found.main >= 2 && !foundSection.includes('-')) {
    return true;
  }
  
  // If found section has higher main number, it's definitely later
  if (found.main > current.main) return true;
  
  // If same main number, check subsection
  if (found.main === current.main && found.sub > current.sub) return true;
  
  return false;
};

/**
 * セクション抽出のフォールバック関数
 */
const extractSectionFallback = (bcpDocument: string, sectionNumber: string): string => {
  console.log(`🔄 Fallback extraction for section: ${sectionNumber}`);
  
  // パターン1: ヘッダー形式を含む柔軟な検索
  const lines = bcpDocument.split('\n');
  let inTargetSection = false;
  const content: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // セクション開始の検出（より包括的なパターン）
    const sectionHeaderMatch = line.match(/^(#{1,6})\s+(\d+-\d+|\d+)\s+/);
    if (sectionHeaderMatch) {
      const foundNumber = sectionHeaderMatch[2];
      if (foundNumber === sectionNumber) {
        inTargetSection = true;
        continue; // ヘッダー行自体は含めない
      } else if (inTargetSection && isLaterSection(sectionNumber, foundNumber)) {
        // より後のセクションが見つかったら終了
        break;
      }
    } else if (line.includes(sectionNumber) && (line.includes('•') || line.includes('-') || line.includes('**') || line.includes('#'))) {
      // 従来のパターンマッチング
      inTargetSection = true;
      continue;
    }
    
    // 次のセクション開始で終了（より厳密）
    if (inTargetSection) {
      // マークダウンヘッダーでの次セクション検出
      if (line.match(/^#{1,6}\s+\d+(-\d+)?\s+/) && !line.includes(sectionNumber)) {
        break;
      }
      
      // リスト形式での次セクション検出
      const nextSectionMatch = line.match(/^[•\-\*]*\s*(\d+-\d+|\d+\.)/);
      if (nextSectionMatch && !line.includes(sectionNumber)) {
        const foundNumber = nextSectionMatch[1].replace('.', '');
        if (isLaterSection(sectionNumber, foundNumber)) {
          break;
        }
      }
      
      // 区切り線で終了
      if (line.match(/^(-{3,}|={3,})\s*$/)) {
        break;
      }
      
      if (line.trim() !== '') {
        content.push(line);
      }
    }
  }
  
  const result = content.join('\n').trim();
  console.log(`🔄 Fallback result length: ${result.length} chars`);
  console.log(`🔄 Fallback preview: "${result.substring(0, 100)}..."`);
  return cleanMarkdownText(result);
};

/**
 * 特定の親セクション内から子セクションを抽出する
 * @param bcpDocument - BCP文書全体
 * @param parentSectionKeywords - 親セクションを特定するキーワード配列
 * @param childSectionNumber - 子セクション番号（例: "2-1", "2-5"）
 * @returns 抽出されたセクション内容
 */
export const extractSectionFromParent = (
  bcpDocument: string,
  parentSectionKeywords: string[],
  childSectionNumber: string
): string => {
  try {
    // まず親セクションを見つける
    const lines = bcpDocument.split('\n');
    let parentSectionStart = -1;
    let parentSectionEnd = lines.length;
    
    // 親セクションの開始位置を探す
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const hasParentKeyword = parentSectionKeywords.some(keyword =>
        line.toLowerCase().includes(keyword.toLowerCase())
      );
      
      if (hasParentKeyword) {
        parentSectionStart = i;
        console.log(`🔍 Found parent section at line ${i}: ${line}`);
        break;
      }
    }
    
    if (parentSectionStart === -1) {
      console.log(`❌ Parent section not found with keywords: ${parentSectionKeywords.join(', ')}`);
      return '';
    }
    
    // 親セクションの終了位置を探す（次の同レベルまたは上位レベルのセクション）
    for (let i = parentSectionStart + 1; i < lines.length; i++) {
      const line = lines[i];
      // "## 9." や "# " や "## 基本版" などの主要セクションヘッダーを検出
      if (line.match(/^#{1,2}\s+\d+\.?\s+/) || line.match(/^#\s+/) || line.match(/^##\s+基本版/)) {
        parentSectionEnd = i;
        console.log(`🛑 Parent section ends at line ${i}: ${line}`);
        break;
      }
    }
    
    // 親セクション内で子セクションを探す
    const parentContent = lines.slice(parentSectionStart, parentSectionEnd).join('\n');
    const result = extractSectionByNumber(parentContent, childSectionNumber);
    
    if (!result) {
      console.log(`⚠️ Child section ${childSectionNumber} not found within parent section`);
      console.log(`Parent content preview: ${parentContent.substring(0, 200)}...`);
    }
    
    return result;
  } catch (error) {
    console.warn('親セクション内からの抽出でエラー:', error);
    return '';
  }
};

/**
 * 文字列の長さを制限し、必要に応じて省略記号を付ける
 * @param text - 対象文字列
 * @param maxLength - 最大文字数
 * @returns 制限された文字列
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
